import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

// Helper to decode HTML entities like &amp; -> &
function decodeHtmlEntities(text) {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

export default function PotCard({ pot }) {
  const totalRupees = (pot.total_raised_paise || 0) / 100;
  const goalRupees = pot.goal_amount_paise ? pot.goal_amount_paise / 100 : null;
  const progressPct = goalRupees ? Math.min((totalRupees / goalRupees) * 100, 100) : 0;

  return (
    <div className="thamboolam-card group" data-testid={`pot-card-${pot.slug}`}>
      {/* Ornate corner accents */}
      <div className="corner-accent top-2 left-2" />
      <div className="corner-accent top-2 right-2 rotate-90" />
      <div className="corner-accent bottom-2 left-2 -rotate-90" />
      <div className="corner-accent bottom-2 right-2 rotate-180" />

      {/* Arched image frame */}
      <Link to={`/p/${pot.slug}`} className="block px-5 pt-5">
        <div className="arched-frame mx-auto">
          {pot.cover_image_url ? (
            <img
              src={pot.cover_image_url}
              alt={pot.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
          ) : (
            <div className="w-full h-full bg-crimson/5 flex items-center justify-center">
              <span className="font-signature text-3xl text-crimson/20">S & A</span>
            </div>
          )}
          {/* Inner shadow overlay */}
          <div className="absolute inset-0 rounded-t-[50%] shadow-[inset_0_0_30px_rgba(0,0,0,0.3)]" />
        </div>
      </Link>

      {/* Content area */}
      <div className="px-5 pt-4 pb-5 text-center">
        <Link to={`/p/${pot.slug}`}>
          <h3 className="font-serif text-lg text-foreground mb-1 group-hover:text-crimson transition-colors" data-testid={`pot-title-${pot.slug}`}>
            {pot.title}
          </h3>
        </Link>
        {pot.story_text && (
          <p className="text-muted-foreground text-xs font-sans line-clamp-2 mb-4 leading-relaxed">
            {pot.story_text}
          </p>
        )}

        {/* Brass groove progress bar */}
        {goalRupees && (
          <div className="mb-4">
            <div className="brass-groove mx-auto">
              <div className="brass-fill" style={{ width: `${Math.min(progressPct, 100)}%` }} />
            </div>
            {totalRupees >= goalRupees ? (
              <div className="text-center mt-2 px-1">
                <span className="text-[11px] font-serif font-semibold text-foreground">
                  {"\u20B9"}{totalRupees.toLocaleString('en-IN')}
                </span>
                {totalRupees > goalRupees ? (
                  <span className="text-[10px] font-sans text-gold ml-1">
                    ({"\u20B9"}{(totalRupees - goalRupees).toLocaleString('en-IN')} over goal)
                  </span>
                ) : (
                  <span className="text-[10px] font-sans text-gold ml-1">
                    Goal reached
                  </span>
                )}
              </div>
            ) : (
              <div className="flex justify-between text-[11px] font-sans mt-2 px-1">
                <span className="font-medium text-foreground">
                  {"\u20B9"}{totalRupees.toLocaleString('en-IN')} raised
                </span>
                <span className="text-muted-foreground">
                  of {"\u20B9"}{goalRupees.toLocaleString('en-IN')}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Contributors as small initials */}
        {pot.contributor_count > 0 && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex -space-x-1.5">
              {pot.contributor_names?.slice(0, 4).map((name, i) => (
                <div
                  key={i}
                  className="w-5 h-5 rounded-full bg-crimson/8 border border-gold/30 flex items-center justify-center"
                  title={decodeHtmlEntities(name)}
                >
                  <span className="text-[9px] font-sans font-bold text-crimson/70">
                    {name.charAt(0).toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
            <span className="text-[11px] text-muted-foreground font-sans">
              {pot.contributor_count} {pot.contributor_count === 1 ? "wish" : "wishes"}
            </span>
          </div>
        )}

        {/* CTA: Single crimson button */}
        <div className="flex items-center justify-center">
          <Link to={`/p/${pot.slug}`}>
            <Button 
              className="text-xs font-sans rounded-full px-6 h-9 bg-crimson hover:bg-crimson/90 text-white border-0" 
              data-testid={`view-pot-${pot.slug}`}
            >
              View Collection
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
